import { getCookie, parseGaClientId, parseGs2SessionSuffix } from './cookies.js';
import { inferTrafficSource } from './detection.js';
import {
    loadStored,
    mergeAttribution,
    readUrlSnapshot,
    saveStored,
    STORAGE_KEY,
} from './storage.js';

/**
 * Measurement ID suffix for _ga_XXXXX cookie (e.g. XC1KC0ML95).
 * Pass your real suffix from GTM / GA4 config.
 */
const DEFAULT_GA4_PROPERTY_SUFFIX = 'XC1KC0ML95';

/**
 * Single run: optionally refresh from URL GET params and cookies → localStorage.
 * Mirrors prior PHP behaviour: save utm_mark when query string present; derive GA IDs from cookies.
 *
 * @param {{
 *   storageKey?: string;
 *   ga4PropertySuffix?: string;
 *   forceUrlCapture?: boolean;
 * }} [options]
 */
export function runAttributionCapture(options = {}) {
    const storageKey = options.storageKey ?? STORAGE_KEY;
    const gaSuffix = options.ga4PropertySuffix ?? DEFAULT_GA4_PROPERTY_SUFFIX;

    const previous = loadStored(storageKey);

    const { utm_mark, utm_params, hasQuery } = readUrlSnapshot();
    const inferred = inferTrafficSource(new URLSearchParams(window.location.search || ''));

    const gaRaw = getCookie('_ga');
    const client_id = parseGaClientId(gaRaw);

    const gaPropName = `_ga_${gaSuffix}`;
    const gaPropRaw = getCookie(gaPropName);
    const client_sess_id = parseGs2SessionSuffix(gaPropRaw);

    /** @type {Partial<import('./storage.js').AttributionSnapshot>} */
    const incoming = {};

    if (hasQuery || options.forceUrlCapture) {
        incoming.utm_mark = utm_mark;
        incoming.utm_params = utm_params;
        incoming.from_google_ads = inferred.fromGoogleAds;
        incoming.from_microsoft_ads = inferred.fromMicrosoftAds;
        incoming.captured_at_iso = new Date().toISOString();
    }

    if (client_id) incoming.client_id = client_id;
    if (client_sess_id) incoming.client_sess_id = client_sess_id;
    if (client_sess_id || client_id) {
        incoming.ga_property_cookie_name = gaPropName;
    }

    if (!previous && !hasQuery && !client_id && !client_sess_id) {
        return loadStored(storageKey);
    }

    const merged = mergeAttribution(previous, incoming);
    saveStored(merged, storageKey);
    syncPlainLocalStorage(merged);
    return merged;
}

/**
 * Plain keys for Make.com / legacy readers (also mirrored from `cv_attribution_v1`).
 * Only sets keys when values exist — never clears first-touch ad ids.
 */
function syncPlainLocalStorage(merged) {
    if (typeof localStorage === 'undefined' || !merged) return;
    try {
        if (merged.client_id) localStorage.setItem('client_id', merged.client_id);
        if (merged.client_sess_id) localStorage.setItem('client_sess_id', merged.client_sess_id);
        const p = merged.utm_params || {};
        const gclid = p.gclid ?? p.GCLID;
        if (gclid) localStorage.setItem('gclid', String(gclid).trim());
        const msclkid = p.msclkid;
        if (msclkid) localStorage.setItem('fromMicrosoftAds', String(msclkid).trim());
    } catch {
        //
    }
}

/**
 * Run on first paint, again after GTM/consent may have set `_ga` cookies, on tab focus, and on SPA navigations.
 */
export function startAttributionCaptureLifecycle() {
    if (typeof window === 'undefined') return;

    const tick = () => runAttributionCapture();

    tick();
    [2000, 5000, 12000, 20000].forEach((ms) => setTimeout(tick, ms));

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') tick();
    });
}

/**
 * Reads current localStorage attribution without mutation.
 */
export function readAttribution(storageKey = STORAGE_KEY) {
    return loadStored(storageKey);
}

export { STORAGE_KEY };
